import type { AnnotationTarget } from './save/annotationDocument.js';

export const ANNOTATION_SETUP_ROUTE = 'annotations';
export const ANNOTATION_EDITOR_ROUTE = 'annotations/edit';
const EDITOR_SEGMENT = '/edit';
const DATASETS_PREFIX = '/datasets/';
const ANNOTATIONS_SEGMENT = '/annotations';
export const ANNOTATION_VIEW_PARAM = 'annotationView';
export const ANNOTATION_SETS_VIEW = 'sets';
export const SOURCE_DATA_ID_PARAM = 'sourceDataId';
export const SOURCE_FILE_ID_PARAM = 'sourceFileId';
export const SOURCE_VERSION_PARAM = 'sourceDatasetVersion';
const ID_KEYS = [
  SOURCE_DATA_ID_PARAM,
  SOURCE_FILE_ID_PARAM,
  'annotationSetId',
  'annotationDataId',
  'documentId',
  'schemaId',
  'kind',
  'fileId',
] as const;
export function annotationWorkspacePath(
  datasetId: string,
  source?: { dataId: string; fileId?: string },
): string {
  const query = new URLSearchParams({ tab: 'annotations' });
  if (source !== undefined) {
    query.set('annotateDataId', source.dataId);
    if (source.fileId) query.set('annotateFileId', source.fileId);
  }
  return `${DATASETS_PREFIX}${encodeURIComponent(datasetId)}?${query.toString()}`;
}
export function annotationSetsPath(datasetId: string): string {
  return `${annotationWorkspacePath(datasetId)}&${new URLSearchParams({
    [ANNOTATION_VIEW_PARAM]: ANNOTATION_SETS_VIEW,
  })}`;
}
export function annotationSetupPath(
  datasetId: string,
  source?: { dataId: string; fileId?: string; version: number },
): string {
  const query = new URLSearchParams();
  if (source !== undefined) {
    query.set(SOURCE_DATA_ID_PARAM, source.dataId);
    if (source.fileId !== undefined)
      query.set(SOURCE_FILE_ID_PARAM, source.fileId);
    query.set(SOURCE_VERSION_PARAM, String(source.version));
  }
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return `${DATASETS_PREFIX}${encodeURIComponent(datasetId)}${ANNOTATIONS_SEGMENT}${suffix}`;
}
export function annotationEditorPath(target: AnnotationTarget): string {
  const query = new URLSearchParams();
  for (const key of ID_KEYS) {
    const value = target[key];
    if (value !== undefined) query.set(key, value);
  }
  query.set(SOURCE_VERSION_PARAM, String(target.sourceDatasetVersion));
  query.set('draftVersion', String(target.draftVersion));
  if (target.legacy) query.set('legacy', 'true');
  return `${annotationSetupPath(target.dataSetId)}${EDITOR_SEGMENT}?${query.toString()}`;
}
export function parseAnnotationTarget(
  dataSetId: string,
  query: URLSearchParams,
): AnnotationTarget {
  const values = Object.fromEntries(
    ID_KEYS.map((key) => [key, query.get(key)]),
  );
  const version = Number(query.get(SOURCE_VERSION_PARAM));
  const draftVersion = Number(query.get('draftVersion'));
  if (
    !dataSetId ||
    ID_KEYS.some((key) => !values[key]) ||
    !Number.isSafeInteger(version) ||
    version < 1 ||
    !Number.isSafeInteger(draftVersion) ||
    draftVersion < 1
  ) {
    throw new Error(
      'Incomplete annotation target. Open annotation setup to select a source and document.',
    );
  }
  return {
    dataSetId,
    sourceDataId: values.sourceDataId!,
    sourceFileId: values.sourceFileId!,
    annotationSetId: values.annotationSetId!,
    annotationDataId: values.annotationDataId!,
    documentId: values.documentId!,
    schemaId: values.schemaId!,
    kind: values.kind!,
    fileId: values.fileId!,
    sourceDatasetVersion: version,
    draftVersion,
    fileName: 'annotations.json',
    legacy: query.get('legacy') === 'true',
  };
}
